import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, CloudinaryImage, type CloudinaryTransformConfig, theme } from '@sg/garnish';

import { type BundleSelectProduct } from '../../Bundle.types';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleProductCard = (props: Props) => {
  const { product, isSelected, onSelect } = props;

  const { name, cost, calories, imageUrl, isOutOfStock } = product;

  const { formatMessage, formatNumber } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const formattedCost = formatNumber(cost / CENTS_IN_DOLLAR, CURRENCY_FORMAT);
  const details = formatMessage(messages.details, { calories, cost: formattedCost });

  const accessibilityState = useMemo(
    () => ({ selected: isSelected, disabled: isOutOfStock }),
    [isOutOfStock, isSelected],
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    onSelect(product);
  }, [onSelect, product]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({
      backgroundColor: isSelected ? theme.colors.OPACITY.KALE.LIGHTEST : undefined,
      opacity: isOutOfStock ? OUT_OF_STOCK_OPACITY : undefined,
    }),
    [isOutOfStock, isSelected],
  );

  const containerStyles = [styles.container, containerDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityState={accessibilityState}
      activeOpacity={CARD_ACTIVE_OPACITY}
      disabled={isOutOfStock}
      style={containerStyles}
      onPress={handlePress}
    >
      <CloudinaryImage
        aria-label={name}
        baseUrl={imageUrl}
        config={IMAGE_TRANSFORM_CONFIG}
        contentFit="contain"
        style={styles.image}
      />

      <View style={styles.details}>
        <BodyText sizeMatch={NAME_SIZE_MATCH} style={styles.name}>
          {name}
        </BodyText>

        <BodyText sizeMatch={DETAILS_SIZE_MATCH} style={styles.detailsText}>
          {details}
        </BodyText>
      </View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CENTS_IN_DOLLAR = 100;
const CURRENCY_FORMAT = { style: 'currency', currency: 'USD' } as const;

const CARD_ACTIVE_OPACITY = 0.8;
const OUT_OF_STOCK_OPACITY = 0.4;
const DETAILS_PADDING_X = 2;
const IMAGE_SIZE = 160;

const IMAGE_MAX_SIZE = 136;
const IMAGE_TRANSFORM_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', height: 0.68, width: 0.38 },
  { width: IMAGE_SIZE },
];

const NAME_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['14'];
const DETAILS_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['12'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing['2'],
    padding: theme.spacing['2'],
    borderWidth: 1,
    borderColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    borderRadius: theme.radius.large,
  },
  image: {
    alignSelf: 'center',
    width: '100%',
    maxHeight: IMAGE_MAX_SIZE,
    aspectRatio: 1,
    borderRadius: theme.radius.large,
  },
  details: {
    gap: theme.spacing['1'],
    paddingHorizontal: DETAILS_PADDING_X,
  },
  name: {
    color: theme.colors.DARK_KALE,
  },
  detailsText: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  details: {
    defaultMessage: '{calories} Cal - {cost}',
    description: 'Bundle | Product card | Calories and price',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  product: BundleSelectProduct;
  isSelected: boolean;
  onSelect: (product: BundleSelectProduct) => void;
};
